import { Navbar, NavbarProps } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { PopoverButtonWithLogo } from "../components/global-sidebar/PopoverSidebar";

import { Flex } from "./Flex";

// ----- Root -----

export interface HexNavBarRootProps extends NavbarProps {
  /** @default false */
  borderless?: boolean;
  /** @default false */
  disabled?: boolean;
}

const Root: React.ComponentType<HexNavBarRootProps> = React.memo(function Root({
  borderless = false,
  disabled = false,
  ...rest
}) {
  return (
    <StyledNavbar $borderless={borderless} $disabled={disabled} {...rest} />
  );
});

const StyledNavbar = styled(Navbar)<{
  $disabled: boolean;
  $borderless: boolean;
}>`
  ${({ $disabled }) => $disabled && `opacity: 20%;`}

  transition: opacity ${({ theme }) => theme.animation.duration} ${({
    theme,
  }) => theme.animation.easing};

  && {
    display: flex;
    flex: none;
    justify-content: space-between;
    min-width: 240px;
    padding: 0;

    height: 40px;
    ${({ theme }) => css`
      @media (max-width: ${theme.mediaQuery.EXTRA_SMALL}px) {
        height: 34px;
      }
    `}

    background-color: transparent;
    box-shadow: ${({ $borderless, theme }) =>
      $borderless ? "none" : theme.boxShadow.NAV_BAR};
  }
`;

// ----- Logo -----

const Logo = React.memo(function HexNavBarLogo() {
  return <PopoverButtonWithLogo />;
});

// ----- Leading Group -----

export interface HexNavBarLeadingGroupProps
  extends React.ComponentPropsWithoutRef<typeof Flex> {
  $newSidebarEnabled?: boolean;
}

const LeadingGroup = styled(Flex)`
  flex: 1;
  height: 100%;
  justify-content: flex-start;

  gap: 8px;
`;
// ----- Middle Group -----

export interface HexNavBarMiddleGroupProps
  extends React.ComponentPropsWithoutRef<typeof Flex> {}

const MiddleGroup = styled(Flex)`
  flex: 1;
  height: 100%;
  justify-content: center;
`;

// ----- Trailing Group -----

export interface HexNavBarTrailingGroupProps
  extends React.ComponentPropsWithoutRef<typeof Flex> {}

const TrailingGroup = styled(Flex)`
  flex: 1;
  height: 100%;
  justify-content: flex-end;
`;

// ----- Export -----

/**
 * A navigation bar component.
 *
 * @example
 * <HexNavBar.Root>
 *   <HexNavBar.LeadingGroup>
 *     <HexNavBar.Logo />
 *     {...}
 *   </HexNavBar.LeadingGroup>
 *   <HexNavBar.MiddleGroup>
 *     {...}
 *   </HexNavBar.MiddleGroup>
 *   <HexNavBar.TrailingGroup>
 *     {...}
 *   </HexNavBar.TrailingGroup>
 *
 * </HexNavBar.Root>
 */
export const HexNavBar = Object.freeze({
  Root: Root,
  Logo: Logo,
  LeadingGroup: LeadingGroup,
  MiddleGroup: MiddleGroup,
  TrailingGroup: TrailingGroup,
});
